import React, { useEffect, useState, memo } from 'react';
import ReactEChartsCore from '../EChart';
import * as echarts from 'echarts/core';
import dayjs from 'dayjs';

import {
  TooltipComponent,
  GridComponent,
  DataZoomComponent,
  TitleComponent
} from 'echarts/components';
import { LineChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([
  TooltipComponent,
  GridComponent,
  DataZoomComponent,
  TitleComponent,
  LineChart,
  CanvasRenderer
]);

const buildOptions = (data, title = 'Live Trend', color = '#00e5ff') => {
  const sorted = [...data].sort((a, b) => new Date(a[0]) - new Date(b[0]));
  const values = sorted.map(x => x[1]);

  const min = Math.min(...values);
  const max = Math.max(...values);

  const padding = (max - min) * 0.15 || 1;

  return {
    backgroundColor: 'transparent',

    title: {
      text: title,
      left: 'center',
      top: 10,
      textStyle: {
        color: '#ddd',
        fontSize: 14,
        fontWeight: 'bold'
      }
    },

    tooltip: {
      trigger: 'axis',
      backgroundColor: '#222',
      borderColor: '#333',
      borderWidth: 1,
      textStyle: { color: '#fff' },
      formatter: function (params) {
        const val = params[0].value[1];

        const safeValue =
          typeof val === 'number'
            ? val.toFixed(2)
            : Number(val || 0).toFixed(2);

        const time = params[0].value[0];

        return `
    <b>${dayjs(time).format('HH:mm:ss')}</b><br/>
    Value: <b>${safeValue}</b>
  `;
      }
    },

    grid: {
      top: 50,
      left: 35,
      right: 20,
      bottom: 40,
      containLabel: true
    },

    dataZoom: [
      {
        type: 'inside',
        xAxisIndex: 0,
        zoomOnMouseWheel: true,
        moveOnMouseWheel: true,
        moveOnMouseMove: true
      }
    ],

    xAxis: {
      type: 'time',
      boundaryGap: false,
      axisLabel: {
        color: '#aaa',
        formatter: value => dayjs(value).format('HH:mm:ss')
      },
      splitLine: {
        show: true,
        lineStyle: { color: '#1e1e1e' }
      }
    },

    yAxis: {
      type: 'value',
      min: min - padding,
      max: max + padding,
      axisLabel: { color: '#aaa' },
      splitLine: {
        lineStyle: { color: '#1e1e1e' }
      }
    },

    series: [
      {
        name: title,
        type: 'line',
        smooth: true,
        showSymbol: false,
        data: sorted,
        lineStyle: {
          width: 2,
          color
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: color + '99' },
            { offset: 1, color: color + '10' }
          ])
        }
      }
    ]
  };
};

const LiveTrendChart = ({ data = [], title, color }) => {
  if (!data || data.length === 0) {
    return (
      <div style={{ color: '#999', textAlign: 'center', padding: 40 }}>
        No data available
      </div>
    );
  }

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={buildOptions(data, title, color)}
      notMerge
      lazyUpdate
      style={{ height: '420px', width: '100%' }}
    />
  );
};

export default memo(LiveTrendChart);
